import logger from '@/other/Logger';

interface DownloadProgress {
  loaded: number;
  total: number;
  percent: number;
}

type ProgressCallback = (progress: DownloadProgress) => void;

export class VideoDownloaderService {
  /**
   * Download a single video file to a Blob, reporting byte progress.
   *
   * The article's recording is always ONE file now (SNAP-2132) — the server
   * merges the per-paragraph segments before handing back a URL — so there is
   * no multi-file/ZIP path here any more.
   */
  public async downloadVideo(url: string, onProgress?: ProgressCallback): Promise<Blob> {
    if (!url) {
      throw new Error('No video URL provided');
    }

    logger.info('Downloading article recording');
    const extension = this.getFileExtension(url);
    const videoData = await this.fetchVideo(url, onProgress);

    return new Blob([videoData.buffer], { type: `video/${extension}` });
  }

  public getFileExtension(url: string): string {
    try {
      const path = new URL(url).pathname;
      const lastDot = path.lastIndexOf('.');
      if (lastDot === -1) return 'mp4';
      return path.substring(lastDot + 1).toLowerCase();
    } catch {
      return 'mp4';
    }
  }

  private async fetchVideo(url: string, onProgress?: ProgressCallback): Promise<Uint8Array> {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const contentLength = Number(response.headers.get('content-length'));
      const reader = response.body?.getReader();
      if (!reader) throw new Error('ReadableStream not supported');

      const chunks: Uint8Array[] = [];
      let receivedLength = 0;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;
        if (onProgress && contentLength > 0) {
          onProgress({
            loaded: receivedLength,
            total: contentLength,
            percent: (receivedLength / contentLength) * 100,
          });
        }
      }

      const result = new Uint8Array(receivedLength);
      let position = 0;
      for (const chunk of chunks) {
        result.set(chunk, position);
        position += chunk.length;
      }
      return result;
    } catch (error) {
      throw new Error(`Failed to download video from ${url}: ${this.getErrorMessage(error)}`);
    }
  }

  private getErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : JSON.stringify(error);
  }
}

const videoDownloaderService = new VideoDownloaderService();
export default videoDownloaderService;
